import { Star, X } from 'lucide-react';

import type { ResearchPollFormat } from '@repo/contracts';

/** The four kinds of poll, in the order the picker and the menu show them. */
export const POLL_TYPES: {
  blurb: string;
  format: ResearchPollFormat;
  /** The longer line under it in "Change Poll". */
  menuBlurb: string;
  title: string;
}[] = [
  {
    blurb: 'Quick gut check',
    format: 'yes-no',
    menuBlurb: 'Quick gut check with fixed answers',
    title: 'Yes / No',
  },
  {
    blurb: 'Find the favourite',
    format: 'choice',
    menuBlurb: 'Each persona chooses one answer',
    title: 'Preference',
  },
  {
    blurb: 'Score it 1–5',
    format: 'rating',
    menuBlurb: 'Score it 1–5, get an average',
    title: 'Give a Rating',
  },
  {
    blurb: 'Put things in order',
    format: 'ranking',
    menuBlurb: 'Put your items in order',
    title: 'Rank these options',
  },
];

export function pollTitle(format: ResearchPollFormat): string {
  return POLL_TYPES.find((type) => type.format === format)?.title ?? '';
}

/**
 * The poll button's first step: what kind of poll, each shown as a tiny
 * result so the choice is about the answer you want back. The kind the
 * question reads as is ringed — a suggestion, never picked for you.
 */
export function PollTypePicker({
  onClose,
  onPick,
  recommended,
}: {
  onClose: () => void;
  onPick: (format: ResearchPollFormat) => void;
  recommended: ResearchPollFormat | null;
}) {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="!m-0 !text-subtitle !font-semibold !text-text-primary">
          Create a Poll – Select Type
        </h3>
        <button
          aria-label="Close"
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {POLL_TYPES.map((type) => {
          const suggested = type.format === recommended;
          return (
            <button
              aria-label={`${type.title}${suggested ? ' (recommended)' : ''}`}
              className={[
                'cursor-pointer rounded-card bg-surface p-2.5 text-left transition hover:bg-warm-alt',
                suggested
                  ? 'ring-2 ring-brand'
                  : 'ring-1 ring-hairline-alt hover:ring-text-muted/40',
              ].join(' ')}
              key={type.format}
              onClick={() => {
                onPick(type.format);
              }}
              type="button"
            >
              <span
                aria-hidden="true"
                className="flex min-h-14 flex-col justify-center rounded-button bg-warm px-3 py-2"
              >
                <Preview format={type.format} />
              </span>
              <span className="mt-2.5 block px-0.5 text-ui-lg font-semibold text-text-primary">
                {type.title}
              </span>
              <span className="block px-0.5 text-caption text-text-secondary">
                {type.blurb}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A made-up result, drawn small: what this kind of poll comes back as. */
function Preview({ format }: { format: ResearchPollFormat }) {
  switch (format) {
    case 'yes-no':
      return (
        <>
          <span className="flex h-2 overflow-hidden rounded-pill bg-hairline">
            <span className="w-2/3 bg-brand" />
          </span>
          <span className="mt-1.5 flex justify-between text-badge font-semibold tracking-wider text-text-secondary">
            <span>Yes 2</span>
            <span>No 1</span>
          </span>
        </>
      );
    case 'choice':
      return (
        <span className="flex flex-col gap-1.5">
          {[
            { bar: 'w-2/3 bg-brand', letter: 'A' },
            { bar: 'w-1/3 bg-hairline-alt', letter: 'B' },
          ].map(({ bar, letter }) => (
            <span className="flex items-center gap-2" key={letter}>
              <span className="w-2 text-badge font-semibold text-text-secondary">
                {letter}
              </span>
              <span className="flex h-1.5 flex-1 rounded-pill bg-tag-minor">
                <span className={`rounded-pill ${bar}`} />
              </span>
            </span>
          ))}
        </span>
      );
    case 'rating':
      return (
        <span className="flex items-center gap-2">
          <span className="text-heading-xs font-semibold text-text-primary">
            3.7
          </span>
          <span className="flex gap-0.5">
            {[0, 1, 2, 3, 4].map((n) => (
              <Star
                className={`size-3 ${n < 4 ? 'text-brand' : 'text-text-muted'}`}
                key={n}
              />
            ))}
          </span>
        </span>
      );
    case 'ranking':
      return (
        <span className="flex flex-col gap-0.5 leading-none">
          {['Hero buttons', 'Navigation', 'Pricing'].map((item, i) => (
            <span
              className="flex items-center gap-1.5 text-badge font-semibold tracking-wider text-text-secondary"
              key={item}
            >
              <span
                className={[
                  'flex size-3.5 items-center justify-center rounded-tab text-micro',
                  i === 0
                    ? 'bg-brand text-white'
                    : 'bg-tag-minor text-text-secondary',
                ].join(' ')}
              >
                {i + 1}
              </span>
              {item}
            </span>
          ))}
        </span>
      );
  }
}
